/**
 * TealerLab — journey recorder.
 *
 * Publishes the tlr_* journey events through tlrEvents.publish (the Shopify.analytics.publish
 * lane of the bus), never through emit: emit fans out to the dataLayer and the AI Hub webhook.
 * Fields, bounds and masking mirror the store-events contract of the analytics pixel
 * (hub: features/analytics/pixel-contract.md): the server drops a whole event on one field out
 * of bounds, so bounds are applied here. A required free-text field that looks personal is sent
 * empty (the server schema refuses it absent); an optional one is left out.
 * Listeners never read layout: page sizes are measured in idle callbacks and cached.
 */
import { tlrEvents } from "@theme/tlr-events";

const ENTRIES = ["pdp", "buy-now", "sticky", "upsell-pdp", "quick-buy", "card", "home-grid", "cart-recos",
  "drawer-upsell", "pack-builder", "bundle-home", "bundle-pdp", "fbt", "gift"];
const PERSONAL = [
  /@/,
  /\d{6,}/,
  /\+?\d(?:[\s.\-()/+]*\d){7,}/,
  /(?:^|[^\d])\d{5}(?:$|[^\d])/,
  // Whole UK postcode only: inward letters exclude C I K M O V, so a quantity like "3ml" stays.
  /(?:^|[^a-z\d])[a-z]{1,2}\d[a-z\d]?\s?\d(?!gr|lb)[abd-hjlnp-uw-z]{2}(?![a-z\d])/i,
];
const ADDRESS_LIKE = /http|:\/\/|\/checkouts|\/account|token/i;
const INERT_PATH = /^(?:\/(?:[a-z]{2}(?:-[a-z]{2})?|\d+)){0,2}\/account(?:\/|$)/i;
const WORD_CHAR = /[\p{L}\p{N}]/u;
const SKIP_TEXT = /^(?:script|style|noscript|template|svg|iframe|input|textarea|select)$/;
const ACTIONABLE = 'a[href], button, summary, label, select, input, textarea, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"]';
const DEAD_TARGETS = 'a[href], button, summary, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="checkbox"], input[type="submit"], input[type="button"], input[type="image"], input[type="reset"]';
const SUBMITS = 'button:not([type]), button[type="submit"], input[type="submit"], input[type="image"]';
const NEWSLETTER_FORMS = "[data-tlr-newsletter-form], #tlr-footer-newsletter, [data-tlr-newsletter] form";
// Tall blocks never reach a 0.5 ratio: the lower steps let "covers half the viewport" be seen.
const THRESHOLDS = [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5];
const PT_KEY = "tlr_rec_pt";
const TYPED_FIELDS = 'input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input:not([type]), textarea';
const NON_UI = /^(?:img|script|iframe|link|style|noscript|meta)$/;

/** `text` cut to `max` code points, without NUL nor lone surrogate (the server refuses both). */
function cutText(text, max) {
  const out = [];
  for (const ch of String(text)) {
    if (out.length >= max) break;
    const c = ch.charCodeAt(0);
    if (ch.length === 2 || (c !== 0 && (c < 0xd800 || c > 0xdfff))) out.push(ch);
  }
  return out.join("");
}

function hasWord(text, word) {
  const t = text.toLowerCase();
  const w = word.toLowerCase();
  for (let i = t.indexOf(w); i !== -1; i = t.indexOf(w, i + 1)) {
    if (!WORD_CHAR.test(t[i - 1] || " ") && !WORD_CHAR.test(t[i + w.length] || " ")) return true;
  }
  return false;
}

function looksPersonal(text, names) {
  if (PERSONAL.some((re) => re.test(text))) return true;
  return (names || []).some((n) => typeof n === "string" && n.length > 1 && hasWord(text, n));
}

/** Folded text cut to `max`, or null when it looks personal: dropped whole, never cut around. */
function maskText(raw, max, opts = {}) {
  if (raw == null) return null;
  const folded = String(raw).slice(0, 2000).replace(/\s+/g, " ").trim();
  if (looksPersonal(folded, opts.names) || (opts.drop && opts.drop.test(folded))) return null;
  if (opts.typed && opts.typed.length) {
    const lower = folded.toLowerCase();
    if (opts.typed.some((v) => lower.includes(v))) return null;
  }
  return cutText(folded, max);
}

/**
 * What the visitor typed, folded and lower-cased, 2 characters at least: the `q` of the URL (the
 * search page prints it in its title) and the current value of every text field of the page.
 * A published text that contains one is dropped like personal data.
 */
function typedValues(search, values) {
  let q = null;
  try {
    q = new URLSearchParams(search || "").get("q");
  } catch (_) {}
  const out = [];
  for (const v of [q, ...(values || [])]) {
    const f = String(v == null ? "" : v).replace(/\s+/g, " ").trim().toLowerCase();
    if (f.length >= 2 && !out.includes(f)) out.push(f);
  }
  return out;
}

function blockId(id) {
  const out = String(id || "").replace(/^shopify-section-/, "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);
  return out || null;
}

/** Shopify ids (digits or GID) from an attribute, unique, 20 at most. */
function parseIds(raw) {
  const out = [];
  for (const d of String(raw || "").match(/\d+/g) || []) {
    if (d.length <= 20 && !out.includes(d)) out.push(d);
    if (out.length === 20) break;
  }
  return out;
}

function pathOf(p) {
  return typeof p === "string" && p.length <= 2000 && /^\/[\x21-\x7e]*$/.test(p) ? p.slice(0, 200) : null;
}

/** Path alone (no query, no fragment) of a link to the same site, else null. */
function hrefPath(href, base) {
  try {
    const url = new URL(href, base);
    return url.origin === new URL(base).origin ? pathOf(url.pathname) : null;
  } catch (_) {
    return null;
  }
}

function sourcePath(url) {
  try {
    const p = new URL(url).pathname;
    return /^\/[\x21-\x7e]*$/.test(p) ? p.slice(0, 120) : null;
  } catch (_) {
    return null;
  }
}

function stackFrame(stack) {
  const m = /(https?:\/\/[^\s)]+?):(\d+):\d+/.exec(String(stack || ""));
  return m ? { url: m[1], line: Number(m[2]) } : null;
}

function pct(v) {
  return Number.isFinite(v) ? Math.round(Math.min(100, Math.max(0, v)) * 10) / 10 : null;
}

function int(v, min, max) {
  return Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : null;
}

function isInertPath(pathname) {
  return INERT_PATH.test(String(pathname || ""));
}

function tagOf(el) {
  const t = String((el && el.localName) || "").toLowerCase();
  return /^[a-z][a-z0-9-]{0,31}$/.test(t) ? t : null;
}

function hookOf(el) {
  const attrs = el.attributes || [];
  for (let i = 0; i < attrs.length; i++) {
    if (/^data-tlr-[a-z0-9-]+$/.test(attrs[i].name)) return `[${attrs[i].name}]`;
  }
  if (typeof el.id === "string" && /^[A-Za-z][\w-]*$/.test(el.id)) return `#${el.id}`;
  const cls = [];
  for (const c of el.classList || []) {
    if (/^-?[A-Za-z_][\w-]*$/.test(c) && !/^(?:is|has)-/.test(c)) cls.push(c);
    if (cls.length === 3) break;
  }
  return cls.length ? `.${cls.join(".")}` : "";
}

/** Short ASCII selector: own data-tlr-* name, id or classes, else the nearest hooked ancestor. */
function selectorOf(el) {
  const tag = tagOf(el) || "";
  const own = hookOf(el);
  if (own) return (tag + own).slice(0, 120);
  let up = el.parentElement;
  for (let i = 0; up && i < 4 && up.localName !== "body" && up.localName !== "html"; i++, up = up.parentElement) {
    const hook = hookOf(up);
    if (hook) return `${tagOf(up) || ""}${hook} ${tag}`.slice(0, 120);
  }
  return tag;
}

function isEditable(el) {
  const v = el.getAttribute && el.getAttribute("contenteditable");
  return v !== null && v !== undefined && v !== "false";
}

/** Text of the element without field values, scripts or hidden subtrees; bounded walk. */
function visibleText(el, max = 1000) {
  let out = "";
  let budget = 500;
  const walk = (node) => {
    for (let n = node.firstChild; n && out.length < max && budget-- > 0; n = n.nextSibling) {
      if (n.nodeType === 3) out += n.nodeValue;
      else if (n.nodeType === 1 && !SKIP_TEXT.test(n.localName) && !n.hidden
        && n.getAttribute("aria-hidden") !== "true" && !isEditable(n)) {
        out += " ";
        walk(n);
      }
    }
  };
  if (!SKIP_TEXT.test(el.localName) && !isEditable(el)) walk(el);
  return out;
}

function labelOf(el, opts = {}) {
  if (el.localName === "body" || el.localName === "html") return "";
  if (el.closest && el.closest('[data-tlr-nolabel], [contenteditable]:not([contenteditable="false"])')) return "";
  const text = visibleText(el).replace(/\s+/g, " ").trim();
  return maskText(text || el.getAttribute("aria-label") || "", 80, opts) ?? "";
}

function formKind(form) {
  if (!form) return "autre";
  const type = (form.querySelector('input[name="form_type"]') || {}).value || "";
  if (type === "customer_login" || /\/account\/login/.test(form.getAttribute("action") || "")) return "login";
  const tags = (form.querySelector('input[name="contact[tags]"]') || {}).value || "";
  if (type === "customer" || /newsletter/i.test(tags) || form.matches(NEWSLETTER_FORMS)) return "newsletter";
  return type === "contact" ? "contact" : "autre";
}

function fieldName(el, opts = {}) {
  return maskText(el.getAttribute("name") || el.id || el.type || el.localName, 40, opts) ?? "";
}

function errorCode(validity) {
  if (!validity) return "autre";
  if (validity.valueMissing) return "required";
  const invalid = ["typeMismatch", "patternMismatch", "tooLong", "tooShort", "rangeOverflow", "rangeUnderflow",
    "stepMismatch", "badInput"];
  return invalid.some((k) => validity[k]) ? "invalid" : "autre";
}

function productsOf(el) {
  const own = parseIds(el.getAttribute("data-tlr-products"));
  if (own.length) return own;
  const out = [];
  const nodes = [el, ...el.querySelectorAll("[data-tlr-products], [data-product-id]")];
  for (const n of nodes) {
    const ids = n.hasAttribute("data-tlr-products")
      ? parseIds(n.getAttribute("data-tlr-products"))
      : parseIds(n.getAttribute("data-product-id")).slice(0, 1);
    for (const id of ids) if (!out.includes(id) && out.length < 20) out.push(id);
  }
  return out;
}

/** Section id, else the nearest id, else the first class (a layout-level modal has neither). */
function blockOf(el) {
  const host = el.closest(".shopify-section") || el.closest("[id]");
  if (host) return blockId(host.id);
  for (const c of el.classList || []) {
    const id = blockId(c);
    if (id) return id;
  }
  return null;
}

function entryOf(t) {
  const host = t.closest("[data-tlr-entry]");
  if (host) {
    const entry = host.getAttribute("data-tlr-entry");
    return ENTRIES.includes(entry) ? { entry, scope: host } : null;
  }
  const card = t.closest("[data-tlr-pcard]");
  return card ? { entry: "card", scope: card } : null;
}

/** Product of the clicked element inside its entry: the nearest single id on the way up. */
function productNear(t, scope) {
  for (let n = t; n; n = n.parentElement) {
    if (n.hasAttribute("data-tlr-products")) {
      const ids = parseIds(n.getAttribute("data-tlr-products"));
      if (ids.length === 1) return ids[0];
    } else if (n.hasAttribute("data-product-id")) {
      const ids = parseIds(n.getAttribute("data-product-id"));
      if (ids.length) return ids[0];
    }
    if (n === scope) break;
  }
  return null;
}

/**
 * Entry only inside an entry or a card; block and product on every click. The entry (or card)
 * bounds the lookup when there is one, so a click joins its exposure; else the section does.
 */
function clickContext(t) {
  const ctx = entryOf(t);
  const scope = ctx ? ctx.scope : t.closest(".shopify-section");
  return { entry: ctx ? ctx.entry : null, block: blockOf(scope || t), product: productNear(t, scope) };
}

function isUiNode(n) {
  if (n.nodeType === 3) return /\S/.test(n.nodeValue || "");
  return n.nodeType === 1 && !NON_UI.test(n.localName);
}

/**
 * A visible reaction at page level: an opening, a change of html, or a change of body or of one
 * of its children. Tracking pixels add and drop <img>/<script> under body on every click: those
 * are not a reaction, or no click would ever be dead.
 */
function pageReaction(r, body, root) {
  const t = r.target;
  if (r.attributeName === "open" || t === root) return true;
  if (t === body) return r.type !== "childList" || [...r.addedNodes, ...r.removedNodes].some(isUiNode);
  return t.parentNode === body && isUiNode(t);
}

function start() {
  const doc = document;
  const root = doc.documentElement;
  const customerName = window.tlr && window.tlr.customer && window.tlr.customer.first_name;
  const names = typeof customerName === "string" && customerName.trim().length > 1 ? [customerName.trim()] : [];
  // Read when a text is published: values change while the visitor types.
  const textOpts = (extra) => ({
    ...extra, names, typed: typedValues(location.search, [...doc.querySelectorAll(TYPED_FIELDS)].map((f) => f.value)),
  });
  const idle = (fn, timeout = 500) =>
    (window.requestIdleCallback ? window.requestIdleCallback(fn, { timeout }) : setTimeout(fn, 200));
  const guard = (fn) => function (e) {
    try {
      return fn.call(this, e);
    } catch (_) {
      return undefined;
    }
  };
  const send = (type, data) => {
    try {
      const out = {};
      for (const k of Object.keys(data)) if (data[k] !== null && data[k] !== undefined) out[k] = data[k];
      if (tlrEvents && typeof tlrEvents.publish === "function") tlrEvents.publish(type, out);
    } catch (_) {}
  };

  // ── Page sizes, measured off the listeners ──
  let dims = null;
  let maxDepth = 0;
  let scrolled = false;
  let measuring = false;
  const waiting = [];
  const tiers = new Set();
  function measure() {
    const h = Math.max(root.scrollHeight, doc.body ? doc.body.scrollHeight : 0, window.innerHeight);
    dims = { w: Math.max(root.scrollWidth, window.innerWidth), h, vw: window.innerWidth, vh: window.innerHeight };
    const depth = h ? Math.min(100, Math.round(((window.scrollY + window.innerHeight) / h) * 100)) : 0;
    if (depth > maxDepth) maxDepth = depth;
    if (scrolled) {
      for (const t of [25, 50, 75, 100]) {
        if (depth >= t && !tiers.has(t)) {
          tiers.add(t);
          send("tlr_scroll", { depth: t });
        }
      }
    }
    while (waiting.length) {
      try {
        waiting.shift()();
      } catch (_) {}
    }
  }
  function scheduleMeasure() {
    if (measuring) return;
    measuring = true;
    idle(guard(() => {
      measuring = false;
      measure();
    }));
  }
  function afterMeasure(fn) {
    if (dims) fn();
    else {
      waiting.push(fn);
      scheduleMeasure();
    }
  }
  const at = (x, y) => ({ x_pct: pct((x / dims.w) * 100), y_pct: pct((y / dims.h) * 100) });

  // ── Exposure ──
  const seen = new Set();
  const watched = new WeakSet();
  const pending = new Set();
  const io = "IntersectionObserver" in window
    ? new IntersectionObserver(guard(onIntersect), { threshold: THRESHOLDS })
    : null;
  function scan() {
    if (!io) return;
    for (const el of doc.querySelectorAll("[data-tlr-entry], [data-tlr-pcard]")) {
      if (watched.has(el)) continue;
      watched.add(el);
      io.observe(el);
    }
    for (const el of pending) {
      pending.delete(el);
      if (el.isConnected) io.observe(el);
    }
  }
  function onIntersect(list) {
    for (const it of list) {
      if (!it.isIntersecting) continue;
      const half = it.intersectionRatio >= 0.5
        || (it.rootBounds && it.intersectionRect.height >= it.rootBounds.height / 2);
      if (!half) continue;
      io.unobserve(it.target);
      expose(it.target);
    }
  }
  function cardProducts(card) {
    const scope = card.closest(".shopify-section") || card.closest("[id]") || card;
    const out = [];
    for (const c of scope.querySelectorAll("[data-tlr-pcard]")) {
      if (c.parentElement && c.parentElement.closest("[data-tlr-entry]")) continue;
      for (const id of productsOf(c)) if (!out.includes(id) && out.length < 20) out.push(id);
    }
    return out;
  }
  function expose(el) {
    const own = el.getAttribute("data-tlr-entry");
    if (own === null && el.parentElement && el.parentElement.closest("[data-tlr-entry]")) return;
    const entry = own === null ? "card" : own;
    if (!ENTRIES.includes(entry)) return;
    const block = blockOf(el);
    if (!block || seen.has(`${entry}|${block}`)) return;
    const products = own === null ? cardProducts(el) : productsOf(el);
    if (!products.length) {
      pending.add(el);
      return;
    }
    seen.add(`${entry}|${block}`);
    send("tlr_exposure", { entry, block, products });
  }
  let scanQueued = false;
  // The entry attribute can be set by a script after the first scan (quick-buy modal).
  new MutationObserver(guard((records) => {
    if (scanQueued || !records.some((r) => r.type === "attributes" || r.addedNodes.length)) return;
    scanQueued = true;
    idle(guard(() => {
      scanQueued = false;
      scan();
      scheduleMeasure();
    }));
  })).observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-tlr-entry"] });

  // ── Clicks: tlr_click, tlr_rage_click, tlr_dead_click ──
  let leaving = false;
  let rageRun = null;
  const watches = new Set();
  let deadObs = null;
  function flushRage() {
    const r = rageRun;
    if (!r) return;
    rageRun = null;
    clearTimeout(r.timer);
    if (!r.ok) return;
    afterMeasure(() => send("tlr_rage_click", {
      target: r.base.target, label: r.base.label, count: Math.min(50, r.times.length), ...at(r.x, r.y),
    }));
  }
  function rage(e, base) {
    const now = e.timeStamp;
    const r = rageRun;
    if (r && now - r.times[r.times.length - 1] <= 1000 && Math.hypot(e.pageX - r.x, e.pageY - r.y) <= 30) {
      if (r.times.length < 50) r.times.push(now);
      const n = r.times.length;
      if (n >= 3 && r.times[n - 1] - r.times[n - 3] <= 1000) r.ok = true;
      clearTimeout(r.timer);
    } else {
      flushRage();
      rageRun = { x: e.pageX, y: e.pageY, times: [now], ok: false, base };
    }
    rageRun.timer = setTimeout(guard(flushRage), 1000);
  }
  function onDeadRecords(records) {
    for (const r of records) {
      const t = r.target;
      const page = pageReaction(r, doc.body, root);
      for (const w of watches) if (page || (w.scope && w.scope.contains(t))) w.hit = true;
    }
  }
  function settle(w) {
    if (deadObs) onDeadRecords(deadObs.takeRecords());
    watches.delete(w);
    if (!watches.size && deadObs) {
      deadObs.disconnect();
      deadObs = null;
    }
    if (w.hit || leaving || location.href !== w.href) return;
    // Read after dispatch: a link or a submit left to the browser navigates.
    if (w.nav && !w.e.defaultPrevented) return;
    send("tlr_dead_click", { target: w.base.target, label: w.base.label, tag: w.base.tag });
  }
  function dead(e, t, base) {
    const el = t.closest(DEAD_TARGETS);
    if (!el) return;
    let nav = el.matches(SUBMITS) && !!el.form;
    if (el.matches("a[href]")) {
      const target = el.getAttribute("target");
      if (el.hasAttribute("download") || (target && target !== "_self")) return;
      try {
        const u = new URL(el.getAttribute("href"), location.href);
        nav = u.origin + u.pathname + u.search !== location.origin + location.pathname + location.search;
      } catch (_) {
        return;
      }
    }
    const scope = el.closest(".shopify-section")
      || [...doc.body.children].find((c) => c.contains(el)) || null;
    const w = { e, nav, scope, href: location.href, hit: false, base };
    watches.add(w);
    if (!deadObs) {
      deadObs = new MutationObserver(guard(onDeadRecords));
      deadObs.observe(root, { subtree: true, childList: true, attributes: true, characterData: true });
    }
    setTimeout(guard(() => settle(w)), 1500);
  }
  function filter(kind, name, value) {
    if (!String(name || "").trim() || !String(value || "").trim()) return;
    const opts = textOpts();
    send("tlr_filter", { kind, name: maskText(name, 40, opts) ?? "", value: maskText(value, 40, opts) ?? "" });
  }
  // Capture phase: runs before the filter section toggles the pill, so its prior state is read.
  function filterClick(t) {
    if (!t.closest("[data-tlr-cfilters]")) return;
    let m = t.closest("[data-tlr-facet-pill]");
    if (m) {
      if (m.getAttribute("aria-pressed") !== "true") filter("filter", m.getAttribute("data-facet"), m.getAttribute("data-value"));
      return;
    }
    m = t.closest("[data-tlr-cfilters-pricep]");
    if (m) {
      if (!m.classList.contains("is-active")) {
        filter("filter", "price", `${m.getAttribute("data-min") || "0"}-${m.getAttribute("data-max") || ""}`);
      }
      return;
    }
    m = t.closest("a.tlr-cfilters__option--link");
    if (m && !m.querySelector(".is-checked")) {
      const label = m.querySelector(".tlr-cfilters__option-label");
      filter("filter", "tag", label ? label.textContent : "");
    }
  }
  function onClick(e) {
    const t = e.target;
    if (!e.isTrusted || !(t instanceof Element)) return;
    filterClick(t);
    if (t.closest("tlr-game")) return;
    const el = t.closest(ACTIONABLE) || t;
    const tag = tagOf(el);
    if (!tag) return;
    const base = { tag, label: labelOf(el, textOpts()), target: selectorOf(el) };
    const link = t.closest("a[href]");
    const data = {
      ...clickContext(t),
      ...base,
      href_path: link ? hrefPath(link.getAttribute("href"), location.href) : null,
    };
    const x = e.pageX;
    const y = e.pageY;
    afterMeasure(() => send("tlr_click", { ...data, ...at(x, y), vw: int(dims.vw, 0, 20000) }));
    if (e.detail > 0) rage(e, base);
    dead(e, t, base);
  }
  doc.addEventListener("click", guard(onClick), { capture: true, passive: true });

  // ── Collection filters and sort ──
  doc.addEventListener("change", guard((e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.closest("[data-tlr-cfilters]")) return;
    if (t.matches("select[data-tlr-cfilters-sort]")) filter("sort", "sort_by", t.value);
    else if (t.matches('input[type="checkbox"][data-tlr-cfilters-input]') && t.checked) {
      const label = t.closest("label") && t.closest("label").querySelector(".tlr-cfilters__option-label");
      filter("filter", t.name, /^gid:/.test(t.value) && label ? label.textContent : t.value);
    }
  }), { capture: true, passive: true });

  // ── Forms: tlr_form_error, tlr_field ──
  let formErrors = 0;
  doc.addEventListener("invalid", guard((e) => {
    const el = e.target;
    if (!(el instanceof Element) || formErrors >= 20) return;
    formErrors++;
    send("tlr_form_error", {
      form: formKind(el.form || el.closest("form")), field: fieldName(el, textOpts()), code: errorCode(el.validity),
    });
  }), { capture: true, passive: true });
  const focused = new WeakMap();
  doc.addEventListener("focusin", guard((e) => {
    const el = e.target;
    if (!(el instanceof Element) || !el.matches("input, select, textarea")) return;
    if (/^(?:hidden|submit|button|reset|image)$/.test(el.type)) return;
    const form = el.form || el.closest("form");
    const key = form || doc;
    const field = fieldName(el, textOpts());
    let set = focused.get(key);
    if (!set) focused.set(key, (set = new Set()));
    if (set.has(field)) return;
    set.add(field);
    send("tlr_field", { form: formKind(form), field });
  }), { passive: true });

  // ── Cart drawer ──
  let drawerState = "close";
  new MutationObserver(guard((records) => {
    for (const r of records) {
      if (r.target.id !== "tlr-cart-drawer") continue;
      const state = r.target.hasAttribute("open") ? "open" : "close";
      if (state === drawerState) continue;
      drawerState = state;
      const cart = window.tlr && window.tlr.cartState;
      const items = int(cart ? cart.itemCount : NaN, 0, 999);
      if (items !== null) send("tlr_drawer", { state, items });
    }
  })).observe(root, { subtree: true, attributes: true, attributeFilter: ["open"] });

  // ── JavaScript errors (own errors never reach here: every handler is guarded) ──
  let errors = 0;
  let inError = false;
  function jsError(message, url, line) {
    if (inError || errors >= 10) return;
    const source = sourcePath(url);
    if (!source || /tlr-recorder/.test(source)) return;
    inError = true;
    try {
      errors++;
      send("tlr_js_error", {
        message: maskText(message, 120, textOpts({ drop: ADDRESS_LIKE })) ?? "",
        source_path: source,
        line: int(line, 0, 1000000) ?? 0,
      });
    } finally {
      inError = false;
    }
  }
  window.addEventListener("error", guard((e) => {
    if (e instanceof ErrorEvent) jsError(e.message, e.filename, e.lineno);
  }), { passive: true });
  window.addEventListener("unhandledrejection", guard((e) => {
    const r = e.reason;
    const frame = r && typeof r.stack === "string" ? stackFrame(r.stack) : null;
    if (frame) jsError(r.message || String(r), frame.url, frame.line);
  }), { passive: true });

  // ── Time on page, published by the next page ──
  let visibleMs = 0;
  let since = doc.visibilityState === "visible" ? performance.now() : null;
  function consumePageTime() {
    let rec = null;
    try {
      rec = JSON.parse(sessionStorage.getItem(PT_KEY) || "null");
      sessionStorage.removeItem(PT_KEY);
    } catch (_) {}
    const prev = rec && pathOf(rec.p);
    if (!prev) return;
    send("tlr_page_time", { visible_ms: int(rec.v, 0, 3600000) ?? 0, max_depth: int(rec.d, 0, 100) ?? 0, prev_path: prev });
  }
  function savePageTime() {
    if (since !== null) {
      visibleMs += performance.now() - since;
      since = null;
    }
    try {
      sessionStorage.setItem(PT_KEY, JSON.stringify({ p: location.pathname, v: Math.round(visibleMs), d: maxDepth }));
    } catch (_) {}
  }
  doc.addEventListener("visibilitychange", guard(() => {
    if (doc.visibilityState === "hidden") {
      for (const w of watches) w.hit = true;
      savePageTime();
    } else since = performance.now();
  }), { passive: true });
  window.addEventListener("pagehide", guard(() => {
    leaving = true;
    for (const w of watches) w.hit = true;
    flushRage();
    savePageTime();
  }), { passive: true });
  window.addEventListener("pageshow", guard((e) => {
    if (!e.persisted) return;
    leaving = false;
    consumePageTime();
    visibleMs = 0;
    maxDepth = 0;
    since = performance.now();
  }), { passive: true });

  window.addEventListener("scroll", guard(() => {
    scrolled = true;
    scheduleMeasure();
  }), { passive: true });
  window.addEventListener("resize", guard(scheduleMeasure), { passive: true });
  window.addEventListener("load", guard(scheduleMeasure), { passive: true });

  consumePageTime();
  idle(guard(() => {
    measure();
    send("tlr_viewport", {
      w: int(window.innerWidth, 0, 20000),
      h: int(window.innerHeight, 0, 20000),
      dpr: Math.round(Math.min(10, Math.max(0, window.devicePixelRatio || 1)) * 100) / 100,
      touch: (navigator.maxTouchPoints || 0) > 0,
    });
    scan();
  }), 2000);
}

try {
  if (typeof window !== "undefined" && typeof document !== "undefined"
    && !isInertPath(location.pathname) && !(window.Shopify && window.Shopify.designMode)) {
    start();
  }
} catch (_) {}

export {
  ENTRIES, cutText, looksPersonal, maskText, typedValues, blockId, parseIds, pathOf, hrefPath, sourcePath, stackFrame,
  pct, int, isInertPath, tagOf, selectorOf, visibleText, labelOf, formKind, fieldName, errorCode, productsOf, blockOf,
  entryOf, clickContext, productNear, isUiNode, pageReaction, ADDRESS_LIKE,
};
